/**
 * Continuous monitoring (/audit/advanced/monitoring): re-run schedule (weekly / monthly) on a baseline audit,
 * last runs, alerts (new issue, regression, fix verified, provider disconnected) and the score trend.
 * Flag audit.monitoring; owners/admins/members change the schedule and acknowledge alerts.
 */
import { useId, useState } from 'react';
import { Link } from 'react-router';
import type { MonitorAlert, MonitorRun, MonitorSchedule } from '@qm/contracts/audit-remediation';
import { Badge, Button, DataTable, EmptyState, ErrorState, Heading, Loading, Toggle } from '@qm/ui';
import { useApp } from '../../../../../lib/app-context';
import { useFormat } from '../../../../../lib/format';
import { actionErrorKey, type TrendPoint } from '../actions/client';
import { AdvancedPage, AUDIT_FLAGS, FailureState, useJobParam, useLoad, useRemediationClient, useRoles, type Load } from '../actions/context';
import { TrendChart } from './TrendChart';
import '../actions/actions.css';

export interface MonitoringData { schedule: MonitorSchedule | null; runs: MonitorRun[]; alerts: MonitorAlert[]; trend: TrendPoint[] }
export const CADENCES = ['weekly', 'monthly'] as const;
/** Alert kinds in customer words: `resolved` = a fix was verified by monitoring. */
export const alertKey = (k: MonitorAlert['kind']) => `audit-remediation.monitoring.alert.${k}`;
export const alertTone = (a: MonitorAlert) => (a.kind === 'resolved' ? 'success' : a.kind === 'regression' ? 'error' : 'warning') as 'success' | 'error' | 'warning';

export function MonitoringPage() {
  return <AdvancedPage area="monitoring" flag={AUDIT_FLAGS.monitoring}><MonitoringScreen /></AdvancedPage>;
}

function MonitoringScreen() {
  const job = useJobParam();
  const load = useLoad('monitoring', async (c) => {
    const [schedule, runs, alerts, trend] = await Promise.all([c.schedule(), c.runs(), c.alerts(), c.trend()]);
    return { schedule, runs, alerts, trend };
  });
  return <MonitoringView load={load} job={job} onChange={(d) => load.set(d)} />;
}

export function MonitoringView({ load, job, onChange }: { load: Load<MonitoringData>; job: string | null; onChange: (d: MonitoringData) => void }) {
  const { t } = useApp();
  const f = useFormat();
  const roles = useRoles();
  const client = useRemediationClient();
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  if (load.status === 'loading') return <Loading label={t('common.loading')} />;
  if (load.status === 'failed') return <FailureState failure={load.failure} />;
  const d = load.data;
  const ack = async (a: MonitorAlert) => {
    setBusy(a.id); setError(null);
    try { const n = await client.ackAlert(a.id); onChange({ ...d, alerts: d.alerts.map((x) => (x.id === n.id ? n : x)) }); }
    catch (e) { setError(actionErrorKey(e)); } finally { setBusy(null); }
  };
  const open = d.alerts.filter((a) => a.status === 'open');
  return (
    <div className="ar-stack">
      <ScheduleCard schedule={d.schedule} job={job} canEdit={roles.write} onSaved={(s) => onChange({ ...d, schedule: s })} />
      <section className="ar-section" aria-labelledby="mon-trend">
        <Heading level={2} id="mon-trend">{t('audit-remediation.monitoring.trend.title')}</Heading>
        {d.trend.length ? <TrendChart points={d.trend} /> : <EmptyState title={t('audit-remediation.monitoring.trend.empty')} />}
      </section>
      <section className="ar-section" aria-labelledby="mon-alerts">
        <Heading level={2} id="mon-alerts">{t('audit-remediation.monitoring.alerts.title', { n: open.length })}</Heading>
        {error ? <ErrorState message={t(error)} /> : null}
        {!d.alerts.length ? <EmptyState title={t('audit-remediation.monitoring.alerts.empty')} /> : (
          <ul className="ar-cards">
            {d.alerts.map((a) => (
              <li key={a.id} className="ar-card" data-testid={`alert-${a.id}`} data-status={a.status}>
                <div className="ar-card__head">
                  <Badge tone={alertTone(a)}>{t(alertKey(a.kind))}</Badge>
                  <Badge tone={a.severity === 'critical' || a.severity === 'high' ? 'error' : 'neutral'}>{t(`audit-remediation.severity.${a.severity}`)}</Badge>
                  <span className="ar-muted">{f.dateTime(a.createdAt)}</span>
                </div>
                <p>{t(`audit-remediation.monitoring.alert.body.${a.kind}`)} <code dir="ltr">{a.checkId}</code></p>
                {a.status === 'open' && roles.write ? (
                  <Button size="sm" disabled={busy === a.id} onClick={() => void ack(a)}>{t('audit-remediation.monitoring.alerts.ack')}</Button>
                ) : a.status !== 'open' ? <p className="ar-muted">{t('audit-remediation.monitoring.alerts.acked', { when: f.dateTime(a.acknowledgedAt) })}</p> : null}
              </li>
            ))}
          </ul>
        )}
      </section>
      <section className="ar-section" aria-labelledby="mon-runs">
        <Heading level={2} id="mon-runs">{t('audit-remediation.monitoring.runs.title')}</Heading>
        {!d.runs.length ? <EmptyState title={t('audit-remediation.monitoring.runs.empty')} /> : (
          <DataTable caption={t('audit-remediation.monitoring.runs.title')} rows={d.runs} columns={[
            { key: 'startedAt', header: t('audit-remediation.monitoring.runs.started'), render: (r) => f.dateTime(r.startedAt ?? r.createdAt) },
            { key: 'status', header: t('audit-remediation.monitoring.runs.status'), render: (r) => <Badge tone={r.status === 'succeeded' ? 'success' : r.status === 'failed' ? 'error' : 'neutral'}>{t(`audit-remediation.monitoring.runs.state.${r.status}`)}</Badge> },
            { key: 'regressions', header: t('audit-remediation.monitoring.runs.regressions'), numeric: true, render: (r) => f.number(r.regressions) },
            { key: 'resolved', header: t('audit-remediation.monitoring.runs.resolved'), numeric: true, render: (r) => f.number(r.resolved) },
            { key: 'errorCode', header: t('audit-remediation.monitoring.runs.error'), render: (r) => (r.errorCode ? <code dir="ltr">{r.errorCode}</code> : '—') },
          ]} />
        )}
      </section>
    </div>
  );
}

function ScheduleCard({ schedule, job, canEdit, onSaved }: { schedule: MonitorSchedule | null; job: string | null; canEdit: boolean; onSaved: (s: MonitorSchedule) => void }) {
  const { t } = useApp();
  const f = useFormat();
  const client = useRemediationClient();
  const id = useId();
  const [cadence, setCadence] = useState<'weekly' | 'monthly'>(schedule?.cadence === 'monthly' ? 'monthly' : 'weekly');
  const [enabled, setEnabled] = useState(schedule?.enabled ?? true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const baseline = schedule?.baselineJobId ?? job;
  const save = async () => {
    if (!baseline) return;
    setBusy(true); setError(null); setSaved(false);
    try { onSaved(await client.saveSchedule({ baselineJobId: baseline, cadence, enabled, checkIds: schedule?.checkIds ?? [] })); setSaved(true); }
    catch (e) { setError(actionErrorKey(e)); } finally { setBusy(false); }
  };
  return (
    <section className="ar-section" aria-labelledby={`${id}-h`} data-testid="schedule">
      <Heading level={2} id={`${id}-h`}>{t('audit-remediation.monitoring.schedule.title')}</Heading>
      {schedule ? (
        <ul className="qs-kv">
          <li><span className="qs-kv__label">{t('audit-remediation.monitoring.schedule.status')}</span><span className="qs-kv__value">{t(schedule.enabled ? 'audit-remediation.monitoring.schedule.on' : 'audit-remediation.monitoring.schedule.off')}</span></li>
          <li><span className="qs-kv__label">{t('audit-remediation.monitoring.schedule.next')}</span><span className="qs-kv__value">{schedule.enabled && schedule.nextRunAt ? f.dateTime(schedule.nextRunAt) : '—'}</span></li>
          <li><span className="qs-kv__label">{t('audit-remediation.monitoring.schedule.last')}</span><span className="qs-kv__value">{schedule.lastRunAt ? f.dateTime(schedule.lastRunAt) : t('audit-remediation.integrations.never')}</span></li>
        </ul>
      ) : <p className="ar-muted">{t('audit-remediation.monitoring.schedule.none')}</p>}
      {!baseline ? (
        <p>{t('audit-remediation.monitoring.schedule.needAudit')} <Link to="/audit">{t('audit-remediation.monitoring.schedule.openAudits')}</Link></p>
      ) : canEdit ? (
        <div className="ar-schedule">
          <fieldset className="ar-radios">
            <legend>{t('audit-remediation.monitoring.schedule.cadence')}</legend>
            {CADENCES.map((c) => (
              <label key={c} className="ar-check"><input type="radio" name={`${id}-cad`} value={c} checked={cadence === c} onChange={() => setCadence(c)} /><span>{t(`audit-remediation.monitoring.schedule.${c}`)}</span></label>
            ))}
          </fieldset>
          <div className="ar-toggle-row">
            <span id={`${id}-en`}>{t('audit-remediation.monitoring.schedule.enabled')}</span>
            <Toggle checked={enabled} onChange={setEnabled} label={t('audit-remediation.monitoring.schedule.enabled')} />
          </div>
          <div className="cu-btnrow"><Button variant="accent" disabled={busy} onClick={() => void save()} data-testid="save-schedule">{t('audit-remediation.monitoring.schedule.save')}</Button></div>
        </div>
      ) : <p className="ar-muted">{t('audit-remediation.monitoring.schedule.readOnly')}</p>}
      <p role="status" aria-live="polite" className="ar-muted">{saved ? t('audit-remediation.monitoring.schedule.saved') : ''}</p>
      {error ? <ErrorState message={t(error)} /> : null}
    </section>
  );
}
